import { useCallback, useEffect, useRef, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from "@stripe/react-stripe-js";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Spinner } from "@/components/shared";

const stripePromise = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY
  ? loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY)
  : null;

export function CheckoutModal({ open, planInterval, onClose, onPaid }) {
  const [sessionId, setSessionId] = useState(null);
  const sessionIdRef = useRef(null);
  const [status, setStatus] = useState("loading"); // loading | ready | processing | paid | error
  const pollRef = useRef(null);

  const fetchClientSecret = useCallback(async () => {
    const { data } = await api.post("/billing/checkout", { plan_interval: planInterval });
    setSessionId(data.session_id);
    sessionIdRef.current = data.session_id;
    setStatus("ready");
    return data.client_secret;
  }, [planInterval]);

  const handleComplete = useCallback(async () => {
    setStatus("processing");
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      const sid = sessionIdRef.current;
      try {
        if (sid) {
          const { data } = await api.get(`/billing/checkout/status/${sid}`);
          if (data.status === "complete" && data.payment_status === "paid") {
            setStatus("paid");
            onPaid?.();
            return;
          }
        }
      } catch (e) { /* keep polling */ }
      if (attempts < 25) pollRef.current = setTimeout(poll, 2000);
      else setStatus("error");
    };
    poll();
  }, [onPaid]);

  useEffect(() => () => { if (pollRef.current) clearTimeout(pollRef.current); }, []);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="checkout-modal">
        <DialogHeader>
          <DialogTitle>Upgrade to Family Path</DialogTitle>
        </DialogHeader>
        {status === "paid" ? (
          <div className="py-10 text-center" data-testid="checkout-success">
            <div className="text-3xl mb-2">🎉</div>
            <h3 className="text-xl font-extrabold text-kid-navy">You're all set!</h3>
            <p className="text-kid-muted mt-1">Welcome to Family Path — all features are unlocked.</p>
          </div>
        ) : status === "processing" ? (
          <div className="py-6"><Spinner label="Confirming your payment…" /></div>
        ) : status === "error" ? (
          <div className="py-10 text-center text-kid-muted" data-testid="checkout-error">
            We couldn't confirm the payment automatically. If you were charged, refresh this page in a moment.
          </div>
        ) : stripePromise ? (
          <div data-testid="checkout-embedded">
            <EmbeddedCheckoutProvider stripe={stripePromise} options={{ fetchClientSecret, onComplete: handleComplete }}>
              <EmbeddedCheckout />
            </EmbeddedCheckoutProvider>
          </div>
        ) : (
          <div className="py-10 text-center text-kid-muted">Payments aren't configured.</div>
        )}
      </DialogContent>
    </Dialog>
  );
}
